/**
 * Stage 1: Date Selection
 *
 * First stage of booking flow - select trip date, time, and party size.
 * For multi-day experiences, uses BookingCalendar with range selection.
 * For single-day experiences, uses standard date input.
 *
 * Location: src/components/booking/flow/stages/Stage1DateSelection.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 * Updated: MVP 1.4.2 SCRUM-408 - Multi-day calendar support
 */

import React, { useEffect, useRef, useMemo, useState } from 'react';
import PricingSummary from '../PricingSummary';
import BookingCalendar from '../../../calendar/BookingCalendar';
import Avatar from '../../../common/Avatar';
import { useCart } from '../../../../contexts/CartContext';
import ConsecutiveDayStrategy from '../../../calendar/strategies/ConsecutiveDayStrategy';

function isConsecutiveBookingEnabled() {
  return import.meta.env.VITE_FEATURE_CONSECUTIVE_BOOKING === 'true';
}

/**
 * Stage1DateSelection Component
 *
 * @param {Object} props
 * @param {Object} props.experience - Experience details
 * @param {Object} props.bookingData - Current booking form data
 * @param {Function} props.onChange - Handler for input changes
 * @param {Object} props.errors - Validation errors
 * @param {Object} props.pricing - Calculated pricing
 * @returns {JSX.Element}
 */
const Stage1DateSelection = ({
  experience,
  bookingData,
  onChange,
  errors = {},
  pricing
}) => {
  const today = new Date().toISOString().split('T')[0];
  const isMultiDay = experience?.duration_type === 'fixed_multi_day';
  const cart = useCart();
  const cartInitialized = useRef(false);
  const [selectedDates, setSelectedDates] = useState([]);

  // SCRUM-709: Create ConsecutiveDayStrategy for cart-aware booking
  const consecutiveStrategy = useMemo(() => {
    if (!isConsecutiveBookingEnabled()) { return null; }
    return new ConsecutiveDayStrategy({
      maxDays: experience?.max_consecutive_days || 7,
      minBookingHours: experience?.bookingLeadTimeHours || experience?.booking_lead_time_hours || 24,
      startDayRestrictions: experience?.start_day_restrictions || null
    });
  }, [experience?.max_consecutive_days, experience?.bookingLeadTimeHours,
      experience?.booking_lead_time_hours, experience?.start_day_restrictions]);

  // SCRUM-709: Initialize cart on mount when consecutive booking is enabled
  useEffect(() => {
    if (!isConsecutiveBookingEnabled() || cartInitialized.current) { return; }
    if (!bookingData.guideId || !bookingData.experienceId) { return; }

    cartInitialized.current = true;
    cart.createCart(bookingData.guideId, bookingData.experienceId);

    // Add initial date to cart if available from URL params
    if (bookingData.startDate) {
      cart.addDate(bookingData.startDate, bookingData.pricePerPerson || 0);
    }
  }, [bookingData.guideId, bookingData.experienceId, bookingData.startDate,
      bookingData.pricePerPerson, cart]);

  // Show BookingCalendar for multi-day OR consecutive booking
  const showCalendar = isMultiDay || isConsecutiveBookingEnabled();

  /**
   * Handle date selection from BookingCalendar
   * SCRUM-709: Syncs with cart when consecutive booking is enabled
   */
  const handleCalendarDateSelect = (startDate, endDate) => {
    const startDateStr = startDate.toISOString().split('T')[0];

    // SCRUM-709: Cart sync for consecutive booking
    if (isConsecutiveBookingEnabled()) {
      // Remove old date from cart if different
      if (bookingData.startDate && bookingData.startDate !== startDateStr) {
        cart.removeDate(bookingData.startDate);
      }
      cart.addDate(startDateStr, bookingData.pricePerPerson || 0);
    }

    onChange('startDate', startDateStr);
    if (endDate) {
      const endDateStr = endDate.toISOString().split('T')[0];
      onChange('endDate', endDateStr);
    }
  };

  /**
   * SCRUM-1192: Handle multi-date selection from BookingCalendar
   * Diffs new selection against previous to call addDate/removeDate
   */
  const handleCalendarDatesSelect = (newDates) => {
    if (!isConsecutiveBookingEnabled()) { return; }

    const toDateKey = (date) => date.toISOString().split('T')[0];
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    // Diff: find added and removed dates
    const added = newDates.filter(d => !selectedDates.some(s => sameDay(s, d)));
    const removed = selectedDates.filter(d => !newDates.some(s => sameDay(s, d)));

    removed.forEach(date => {
      cart.removeDate(toDateKey(date));
    });
    added.forEach(date => {
      cart.addDate(toDateKey(date), bookingData.pricePerPerson || 0);
    });

    setSelectedDates(newDates);

    // Update parent with all selected date keys
    onChange('selectedDates', newDates.map(toDateKey));

    // Also set startDate to first date for compatibility
    if (newDates.length > 0) {
      onChange('startDate', toDateKey(newDates[0]));
    }
  };

  /**
   * SCRUM-709: Handle date input change with cart sync
   */
  const handleDateInputChange = (e) => {
    const newDate = e.target.value;
    if (isConsecutiveBookingEnabled()) {
      if (bookingData.startDate && bookingData.startDate !== newDate) {
        cart.removeDate(bookingData.startDate);
      }
      if (newDate) {
        cart.addDate(newDate, bookingData.pricePerPerson || 0);
      }
    }
    onChange('startDate', newDate);
  };

  return (
    <div className="space-y-6">
      {/* Experience Header */}
      <div className="flex items-start gap-4">
        <Avatar
          src={experience.guidePhoto}
          name={experience.guideName}
          size="xl"
        />
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">{experience.name}</h2>
          <p className="text-gray-600">
            with {experience.guideName}
            {experience.guideRating && (
              <span className="ml-2">
                ⭐ {experience.guideRating} ({experience.guideReviews} reviews)
              </span>
            )}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {experience.duration} • Max {experience.maxGuests} guests
          </p>
        </div>
      </div>

      {/* Trip Date - Multi-day uses BookingCalendar, single-day uses date input */}
      <div>
        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
          {isMultiDay ? 'Trip Dates' : 'Trip Date'} <span className="text-red-500">*</span>
        </label>

        {showCalendar ? (
          <BookingCalendar
            guideId={experience.guideId}
            guideServiceId={experience.guideServiceId}
            selectedDate={bookingData.startDate ? new Date(bookingData.startDate + 'T00:00:00') : null}
            onDateSelect={handleCalendarDateSelect}
            onDatesSelect={isConsecutiveBookingEnabled() ? handleCalendarDatesSelect : undefined}
            experience={experience}
            strategy={isConsecutiveBookingEnabled() ? consecutiveStrategy : undefined}
          />
        ) : (
          <>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={bookingData.startDate || ''}
              onChange={handleDateInputChange}
              min={today}
              required
              className={`w-full px-4 py-2.5 rounded-lg border-2 transition-colors duration-200 ${
                errors.startDate
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'
              }`}
            />
            {errors.startDate && (
              <p className="mt-1.5 text-sm text-red-600">{errors.startDate}</p>
            )}
          </>
        )}
      </div>

      {/* Party Size */}
      <div>
        <label htmlFor="partySize" className="block text-sm font-medium text-gray-700 mb-1">
          Number of Guests <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => {
              const minGuests = experience.minGuests || 1;
              const newSize = Math.max(minGuests, (bookingData.partySize || minGuests) - 1);
              onChange('partySize', newSize);
            }}
            disabled={bookingData.partySize <= (experience.minGuests || 1)}
            className="w-10 h-10 rounded-lg border-2 border-gray-300 flex items-center justify-center font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            −
          </button>

          <input
            type="number"
            id="partySize"
            name="partySize"
            value={bookingData.partySize || (experience.minGuests || 1)}
            onChange={(e) => onChange('partySize', parseInt(e.target.value, 10))}
            min={experience.minGuests || 1}
            max={experience.maxGuests}
            required
            className="w-20 text-center px-4 py-2.5 rounded-lg border-2 border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
          />

          <button
            type="button"
            onClick={() => {
              const newSize = Math.min(experience.maxGuests, (bookingData.partySize || 1) + 1);
              onChange('partySize', newSize);
            }}
            disabled={bookingData.partySize >= experience.maxGuests}
            className="w-10 h-10 rounded-lg border-2 border-gray-300 flex items-center justify-center font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            +
          </button>

          <span className="text-sm text-gray-500">
            {experience.minGuests > 1 ? `(${experience.minGuests}–${experience.maxGuests} guests)` : `(max ${experience.maxGuests})`}
          </span>
        </div>
        {errors.partySize && (
          <p className="mt-1.5 text-sm text-red-600">{errors.partySize}</p>
        )}
      </div>

      {/* Pricing Summary */}
      {pricing && (
        <PricingSummary
          perPersonCents={pricing.perPerson}
          numParticipants={bookingData.partySize || 1}
          subtotalCents={pricing.subtotal}
          platformFeeCents={pricing.platformFee}
          totalCents={pricing.total}
        />
      )}
    </div>
  );
};

export default Stage1DateSelection;
